import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import api from '../api';

export const useSEOConfig = () => {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadConfig = async () => {
      try {
        const response = await api.get('/seo/config-public');
        if (isMounted) {
          setConfig(response.data?.data || {});
        }
      } catch (error) {
        if (isMounted) {
          setConfig({});
        }
      }
    };

    loadConfig();

    return () => {
      isMounted = false;
    };
  }, []);

  return config;
};

/**
 * Hook para gerenciar meta tags SEO globais e por página
 * @param {Object} options - Opções de SEO
 * @param {string} options.title - Título da página
 * @param {string} options.description - Descrição meta
 * @param {string} options.keywords - Palavras-chave
 * @param {string} options.image - URL da imagem para Open Graph
 * @param {string} options.url - URL canônica
 * @param {boolean} options.fetchGlobal - Se deve buscar configurações globais do backend
 * @param {Object} options.schema - Dados para JSON-LD/Schema.org
 */
export const useSEO = (options = {}) => {
  const {
    title = '',
    description = '',
    keywords = '',
    image = '',
    url = '',
    schema = null,
  } = options;

  const config = useSEOConfig();

  // Determinar valores finais (usar os passados como parâmetro ou usar padrões)
  const finalTitle =
    title ||
    config?.site_title ||
    'Fracaro Store - Jogos de Cama, Mesa e Banho';
  const finalDescription =
    description ||
    config?.site_description ||
    'Compre jogos de cama, mesa e banho em renda e tecido de alta qualidade. Oferecemos produtos sofisticados para deixar sua casa ainda mais elegante. Frete grátis e condições especiais!';
  const finalUrl = url || window.location.href;
  const finalImage =
    image ||
    config?.og_image ||
    config?.twitter_image ||
    `${window.location.origin}/assets/image/LogoFracaroStore.svg`;
  const finalKeywords = keywords || config?.site_keywords || '';
  const finalOgTitle = title || config?.og_title || finalTitle;
  const finalOgDescription =
    description || config?.og_description || finalDescription;
  const finalTwitterTitle = title || config?.twitter_title || finalTitle;
  const finalTwitterDescription =
    description || config?.twitter_description || finalDescription;
  const finalTwitterImage = image || config?.twitter_image || finalImage;
  const finalOgType = config?.og_type || 'website';
  const finalOgSiteName =
    config?.og_site_name || 'Fracaro Store';
  const finalTwitterCard =
    config?.twitter_card || 'summary_large_image';
  const finalTwitterSite = config?.twitter_site || '';
  const finalFavicon16 = config?.favicon_16 || '';
  const finalFavicon32 = config?.favicon_32 || '';
  const finalFavicon64 = config?.favicon_64 || '';
  const finalAppleTouchIcon = config?.apple_touch_icon || '';
  const organizationLogo =
    config?.apple_touch_icon || config?.og_image || '';

  return {
    title: finalTitle,
    description: finalDescription,
    keywords: finalKeywords,
    image: finalImage,
    url: finalUrl,
    schema,
    ogTitle: finalOgTitle,
    ogDescription: finalOgDescription,
    ogType: finalOgType,
    ogSiteName: finalOgSiteName,
    twitterCard: finalTwitterCard,
    twitterTitle: finalTwitterTitle,
    twitterDescription: finalTwitterDescription,
    twitterImage: finalTwitterImage,
    twitterSite: finalTwitterSite,
    favicon16: finalFavicon16,
    favicon32: finalFavicon32,
    favicon64: finalFavicon64,
    appleTouchIcon: finalAppleTouchIcon,
    organizationLogo,
  };
};

/**
 * Componente para renderizar tags de SEO com React Helmet
 */
export const SEOHelmet = ({
  title,
  description,
  keywords,
  image,
  url,
  schema,
  children,
}) => {
  const seo = useSEO({
    title,
    description,
    keywords,
    image,
    url,
    schema,
  });
  const canonicalUrl = seo.url || window.location.href;
  const resolvedSchema = useMemo(() => {
    if (schema) {
      return schema;
    }

    if (!seo.organizationLogo) {
      return null;
    }

    return {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: seo.ogSiteName || seo.title || 'Fracaro Store',
      url: window.location.origin,
      logo: seo.organizationLogo,
    };
  }, [schema, seo.organizationLogo, seo.title]);

  return (
    <Helmet>
      {/* Tags básicas */}
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      {seo.keywords && <meta name="keywords" content={seo.keywords} />}
      <meta name="viewport" content="width=device-width, initial-scale=1" />

      {seo.favicon16 && (
        <link rel="icon" type="image/png" sizes="48x48" href={seo.favicon16} />
      )}
      {seo.favicon32 && (
        <link rel="icon" type="image/png" sizes="96x96" href={seo.favicon32} />
      )}
      {seo.favicon64 && (
        <link rel="icon" type="image/png" sizes="192x192" href={seo.favicon64} />
      )}
      {seo.appleTouchIcon && (
        <link rel="apple-touch-icon" href={seo.appleTouchIcon} />
      )}

      {/* Open Graph (Facebook, LinkedIn, etc.) */}
      <meta property="og:type" content={seo.ogType} />
      <meta property="og:title" content={seo.ogTitle} />
      <meta property="og:description" content={seo.ogDescription} />
      {seo.image && <meta property="og:image" content={seo.image} />}
      {seo.image && <meta property="og:image:secure_url" content={seo.image} />}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={seo.ogSiteName} />

      {/* Twitter Card */}
      <meta name="twitter:card" content={seo.twitterCard} />
      <meta name="twitter:title" content={seo.twitterTitle} />
      <meta name="twitter:description" content={seo.twitterDescription} />
      {seo.twitterSite && (
        <meta name="twitter:site" content={seo.twitterSite} />
      )}
      {seo.twitterImage && (
        <meta name="twitter:image" content={seo.twitterImage} />
      )}

      {/* Link canônico */}
      <link rel="canonical" href={canonicalUrl} />

      {/* JSON-LD Schema */}
      {resolvedSchema && (
        <script type="application/ld+json">
          {JSON.stringify(resolvedSchema)}
        </script>
      )}

      {children}
    </Helmet>
  );
};

/**
 * Componente global que injeta favicon e apple-touch-icon em TODAS as páginas.
 * Deve ser renderizado uma vez no nível raiz (ex: routes.js).
 */
export const GlobalFavicon = () => {
  const config = useSEOConfig();

  if (!config) return null;

  const favicon16 = config.favicon_16 || '';
  const favicon32 = config.favicon_32 || '';
  const favicon64 = config.favicon_64 || '';
  const appleTouchIcon = config.apple_touch_icon || '';

  return (
    <Helmet>
      {favicon16 && <link rel="icon" type="image/png" sizes="48x48" href={favicon16} />}
      {favicon32 && <link rel="icon" type="image/png" sizes="96x96" href={favicon32} />}
      {favicon64 && <link rel="icon" type="image/png" sizes="192x192" href={favicon64} />}
      {appleTouchIcon && <link rel="apple-touch-icon" href={appleTouchIcon} />}
    </Helmet>
  );
};

/**
 * Gerador de schema JSON-LD para diferentes tipos de conteúdo
 */
export const generateSchema = (type, data = {}) => {

  const baseUrl = window.location.origin;

  switch (type) {
    case 'product':
      return {
        '@context': 'https://schema.org/',
        '@type': 'Product',
        name: data.name || '',
        description: data.description || '',
        image: data.image || '',
        brand: {
          '@type': 'Brand',
          name: 'Fracaro Store',
        },
        offers: {
          '@type': 'Offer',
          url: data.url || '',
          priceCurrency: data.currency || 'BRL',
          price: data.price || '',
          availability: data.inStock
            ? 'InStock'
            : 'OutOfStock',
          seller: {
            '@type': 'Organization',
            name: 'Fracaro Store',
          },
        },
        aggregateRating: data.rating && {
          '@type': 'AggregateRating',
          ratingValue: data.rating.value || '',
          reviewCount: data.rating.count || 0,
        },
      };

    case 'organization':
      return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Fracaro Store',
        url: baseUrl,
        logo: `${baseUrl}/assets/logo/LogoFracaroStore.png`,
        description:
          'Compre jogos de cama, mesa e banho em renda e tecido de alta qualidade.',
        sameAs: [
          data.facebook || '',
          data.instagram || '',
          data.twitter || '',
        ].filter(Boolean),
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'Customer Service',
          telephone: data.phone || '',
          email: data.email || '',
        },
      };

    case 'breadcrumb':
      return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: (data.items || []).map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: `${baseUrl}${item.url}`,
        })),
      };

    case 'faq':
      return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: (data.items || []).map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      };

    default:
      return null;
  }
};
